import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { SaveConfirmationModal, DeleteConfirmationModal, useNotification } from '../common';

const CompanySettings = () => {
  // Removed useContext usage
  // Import useNotification hook
  const { showNotification } = useNotification();

  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_vat: '',
    company_bank_account: '',
    company_swift: '',
    company_about: ''
  });
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [bankAccountForm, setBankAccountForm] = useState({
    country_code: '',
    bank_account: '',
    swift_bic: '',
    bank_name: '',
    currency: 'NOK',
    is_default: false
  });
  const [savingBankAccount, setSavingBankAccount] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  
  // Logo state
  const [primaryLogo, setPrimaryLogo] = useState(null);
  const [primaryLogoPreview, setPrimaryLogoPreview] = useState('');
  const [alternativeLogo, setAlternativeLogo] = useState(null);
  const [alternativeLogoPreview, setAlternativeLogoPreview] = useState('');
  // Removed companyId state
  const [loading, setLoading] = useState(true);
  const [deletePrimaryLoading, setDeletePrimaryLoading] = useState(false);
  const [deleteAlternativeLoading, setDeleteAlternativeLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showDeletePrimaryLogoConfirmation, setShowDeletePrimaryLogoConfirmation] = useState(false);
  const [showDeleteAlternativeLogoConfirmation, setShowDeleteAlternativeLogoConfirmation] = useState(false);
  
  // Logo usage configuration
  const [logoUsage, setLogoUsage] = useState({
    sidebar: 'primary',
    invoices: 'primary',
    email: 'primary'
  });
  
  // State for sidebar color
  const [sidebarColor, setSidebarColor] = useState('#1e40af'); // Default blue-800

  // Country settings
  const [companyCountries, setCompanyCountries] = useState(['Norway', 'Sweden', 'Germany']);

  // Available countries for selection
  const availableCountries = [
    'Norway', 'Sweden', 'Germany', 'United States',
    'Austria', 'Switzerland', 'Luxembourg', 'Denmark', 'Finland', 'Australia'
  ].sort();

  // File input refs
  const primaryLogoInputRef = useRef(null);
  const alternativeLogoInputRef = useRef(null);
  
  // Function to fetch company countries
  const fetchCompanyCountries = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = {
        headers: {
          'x-auth-token': token
        }
      };

      const res = await axios.get('/api/settings/company/countries', config);
      setCompanyCountries(res.data);
    } catch (error) {
      console.error('Error fetching company countries:', error);
      // Keep default countries if fetch fails
    }
  };

  // Fetch bank accounts
  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      const res = await axios.get('/api/settings/company/bank-accounts', config);
      setBankAccounts(res.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      showNotification('Failed to fetch bank accounts', 'error');
    }
  };

  // Save bank account
  const saveBankAccount = async () => {
    if (!bankAccountForm.country_code || !bankAccountForm.bank_account) {
      showNotification('Country code and bank account are required', 'error');
      return;
    }
    
    setSavingBankAccount(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      await axios.post('/api/settings/company/bank-accounts', bankAccountForm, config);
      
      // Reset form and refresh list
      setBankAccountForm({
        country_code: '',
        bank_account: '',
        swift_bic: '',
        bank_name: '',
        currency: 'NOK',
        is_default: false
      });
      setShowBankAccountForm(false);
      setEditingBankAccount(null);
      await fetchBankAccounts();
      
      showNotification(editingBankAccount ? 'Bank account updated successfully' : 'Bank account saved successfully', 'success');
    } catch (error) {
      console.error('Error saving bank account:', error);
      showNotification('Failed to save bank account', 'error');
    } finally {
      setSavingBankAccount(false);
    }
  };

  // Edit bank account
  const editBankAccount = (account) => {
    setBankAccountForm({
      country_code: account.country_code,
      bank_account: account.bank_account,
      swift_bic: account.swift_bic || '',
      bank_name: account.bank_name || '',
      currency: account.currency || 'NOK',
      is_default: account.is_default || false
    });
    setEditingBankAccount(account.country_code);
    setShowBankAccountForm(true);
  };

  // Cancel edit
  const cancelBankAccountForm = () => {
    setBankAccountForm({
      country_code: '',
      bank_account: '',
      swift_bic: '',
      bank_name: '',
      currency: 'NOK',
      is_default: false
    });
    setEditingBankAccount(null);
    setShowBankAccountForm(false);
  };

  // Delete bank account
  const deleteBankAccount = async (countryCode) => {
    if (!window.confirm(`Are you sure you want to delete the bank account for ${countryCode}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      await axios.delete(`/api/settings/company/bank-accounts/${countryCode}`, config);
      await fetchBankAccounts();
      
      showNotification('Bank account deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting bank account:', error);
      showNotification('Failed to delete bank account', 'error');
    }
  };

  // Available countries for bank accounts
  const bankAccountCountries = [
    { code: 'NO', name: 'Norway' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'DE', name: 'Germany' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' }
  ];

  // Fetch company settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Removed company ID fetching logic from here
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          setError('Authentication token not found.');
          return;
        }

        const config = {
          headers: {
            'x-auth-token': token
          }
        };

        const res = await axios.get('/api/settings/company', config);

        // Fetch company countries and bank accounts in parallel
        fetchCompanyCountries();
        fetchBankAccounts();

        // Set form data fields from API response
        const newFormData = {};
        
        // Map known fields
        Object.keys(formData).forEach(key => {
          newFormData[key] = res.data[key] || '';
        });
        
        setFormData(newFormData);
        
        // Get logo usage configuration from API response or use defaults
        if (res.data.logo_usage) {
          try {
            // The server might return the config as a JSON string or an actual object
            let usageConfig;
            
            if (typeof res.data.logo_usage === 'string') {
              // Try to parse the string as JSON
              try {
                usageConfig = JSON.parse(res.data.logo_usage);
                console.log('Parsed logo usage config from string:', usageConfig);
              } catch (parseError) {
                console.error('Error parsing logo usage config string:', parseError);
                // If parsing fails, keep the defaults
                usageConfig = {
                  sidebar: 'primary',
                  invoices: 'primary',
                  email: 'primary'
                };
              }
            } else if (typeof res.data.logo_usage === 'object') {
              // It's already an object
              usageConfig = res.data.logo_usage;
              console.log('Logo usage config is already an object:', usageConfig);
            }
            
            // Only update state if we have a valid config object
            if (usageConfig && typeof usageConfig === 'object') {
              setLogoUsage(usageConfig);
            }
          } catch (e) {
            console.error('Error processing logo usage config:', e);
          }
        }
        
        // Get sidebar color setting if available
        if (res.data.sidebar_color) {
          setSidebarColor(res.data.sidebar_color);
          console.log('Loaded sidebar color:', res.data.sidebar_color);
        }
        
        // Handle primary logo preview
        if (res.data.company_logo) {
          let logoPath = res.data.logo_details ? res.data.logo_details.path : res.data.company_logo;
          console.log("Original primary logo path:", logoPath);
          
          // If it's a relative path and not a full URL, make sure it points to the right server
          if (logoPath.startsWith('/uploads/')) {
            // In development, point to the backend server port
            const isDevEnvironment = process.env.NODE_ENV === 'development';
            if (isDevEnvironment) {
              // In development, add the backend server URL
              logoPath = `http://localhost:5005${logoPath}`;
            }
            // In production, the path is already correct because both frontend and backend are served from the same domain
          }
          
          console.log("Final primary logo preview path:", logoPath);
          setPrimaryLogoPreview(logoPath);
        }
        
        // Handle alternative logo preview
        if (res.data.company_logo_alt) {
          console.log("Alternative logo found in response:", res.data.company_logo_alt);
          
          // Check if the API already provided details about the alternative logo
          if (res.data.alt_logo_details && res.data.alt_logo_details.path) {
            let logoPath = res.data.alt_logo_details.path;
            
            // If it's a relative path and not a full URL, make sure it points to the right server
            if (logoPath.startsWith('/uploads/')) {
              const isDevEnvironment = process.env.NODE_ENV === 'development';
              if (isDevEnvironment) {
                // In development, add the backend server URL
                logoPath = `http://localhost:5005${logoPath}`;
              }
              // In production, the path is already correct because both frontend and backend are served from the same domain
            }
            
            console.log("Setting alternative logo preview from alt_logo_details:", logoPath);
            setAlternativeLogoPreview(logoPath);
          }
          // If we didn't get details but have a direct alt_logo_url
          else if (res.data.alt_logo_url) {
            let logoPath = res.data.alt_logo_url;
            
            // If it's a relative path and not a full URL, make sure it points to the right server
            if (logoPath.startsWith('/uploads/')) {
              const isDevEnvironment = process.env.NODE_ENV === 'development';
              if (isDevEnvironment) {
                // In development, add the backend server URL
                logoPath = `http://localhost:5005${logoPath}`;
              }
              // In production, the path is already correct because both frontend and backend are served from the same domain
            }
            
            console.log("Setting alternative logo preview from alt_logo_url:", logoPath);
            setAlternativeLogoPreview(logoPath);
          }
          // Check if the value is a direct Cloudinary URL
          else if (res.data.company_logo_alt.includes('cloudinary.com')) {
            console.log("Detected Cloudinary URL for alternative logo:", res.data.company_logo_alt);
            setAlternativeLogoPreview(res.data.company_logo_alt);
          }
          // Fallback to old method (legacy support)
          else {
            const altLogoId = res.data.company_logo_alt;
            
            if (altLogoId.startsWith('/uploads/')) {
              // Direct path
              let logoPath = altLogoId;
              
              // If it's a relative path and not a full URL, make sure it points to the right server
              const isDevEnvironment = process.env.NODE_ENV === 'development';
              if (isDevEnvironment) {
                // In development, add the backend server URL
                logoPath = `http://localhost:5005${logoPath}`;
              }
              // In production, the path is already correct because both frontend and backend are served from the same domain
              
              console.log("Setting alternative logo preview from direct path:", logoPath);
              setAlternativeLogoPreview(logoPath);
            } else {
              try {
                // Fetch file details for the alternative logo
                console.log("Fetching alternative logo details for ID:", altLogoId);
                const logoRes = await axios.get(`/api/files/${altLogoId}`, config);
                if (logoRes.data && logoRes.data.path) {
                  let logoPath = logoRes.data.path;
                  
                  // If it's a relative path and not a full URL, make sure it points to the right server
                  if (logoPath.startsWith('/uploads/')) {
                    const isDevEnvironment = process.env.NODE_ENV === 'development';
                    if (isDevEnvironment) {
                      // In development, add the backend server URL
                      logoPath = `http://localhost:5005${logoPath}`;
                    }
                    // In production, the path is already correct because both frontend and backend are served from the same domain
                  }
                  
                  console.log("Setting alternative logo preview from API response:", logoPath);
                  setAlternativeLogoPreview(logoPath);
                }
              } catch (logoErr) {
                console.error('Error fetching alternative logo details:', logoErr);
              }
            }
          }
        }
        // If companyId wasn't set from context, fetch user profile (optional, depends on AuthContext setup)
        // Example: Fetching user profile if needed
        // if (!companyId) {
        //   const userRes = await axios.get('/api/auth/me', config); // Adjust endpoint if needed
        //   if (userRes.data && userRes.data.company_id) {
        //     setCompanyId(userRes.data.company_id);
        //   }
        // }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching company settings:', err);
        setError('Failed to load company settings');
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Removed webhookUrl definition and copyToClipboard function

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle primary logo file selection
  const handlePrimaryLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrimaryLogo(file);
      setPrimaryLogoPreview(URL.createObjectURL(file));
    }
  };
  
  // Handle alternative logo file selection
  const handleAlternativeLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAlternativeLogo(file);
      setAlternativeLogoPreview(URL.createObjectURL(file));
    }
  };
  
  // Handle logo usage change
  const handleLogoUsageChange = (location, value) => {
    setLogoUsage(prev => ({
      ...prev,
      [location]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSaveConfirmation(true);
  };
  
  // Execute the actual save operation
  const executeSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Send text fields using a regular JSON payload
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      console.log('Submitting text data to server...');
      await axios.post('/api/settings/company/text', formData, config);
      
      // Save logo usage configuration
      console.log('Saving logo usage configuration...', logoUsage);
      await axios.post('/api/settings/company/logo-usage', {
        logoUsage: logoUsage
      }, config);
      
      // Save sidebar color
      console.log('Saving sidebar color...', sidebarColor);
      await axios.post('/api/settings/company/sidebar-color', {
        sidebarColor: sidebarColor
      }, config);
      
      // Save company countries
      console.log('Saving company countries...', companyCountries);
      await axios.post('/api/settings/company/countries', { 
        countries: companyCountries 
      }, config);
      
      // Process the primary logo upload
      if (primaryLogo) {
        console.log('Uploading primary logo as Base64...');
        
        // Read the file as a data URL (Base64)
        const reader = new FileReader();
        
        // Create a promise to handle the async FileReader
        const fileReadPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
        });
        
        reader.readAsDataURL(primaryLogo);
        
        // Wait for the file to be read
        const base64Data = await fileReadPromise;
        
        // Send the Base64 data to the server
        await axios.post('/api/settings/company/logo-base64', {
          filename: primaryLogo.name,
          mimeType: primaryLogo.type,
          size: primaryLogo.size,
          data: base64Data
        }, config);
      }
      
      // Process the alternative logo upload
      if (alternativeLogo) {
        console.log('Uploading alternative logo as Base64...');
        
        // Read the file as a data URL (Base64)
        const reader = new FileReader();
        
        // Create a promise to handle the async FileReader
        const fileReadPromise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
        });
        
        reader.readAsDataURL(alternativeLogo);
        
        // Wait for the file to be read
        const base64Data = await fileReadPromise;
        
        // Send the Base64 data to the server
        await axios.post('/api/settings/company/logo-alt-base64', {
          filename: alternativeLogo.name,
          mimeType: alternativeLogo.type,
          size: alternativeLogo.size,
          data: base64Data
        }, config);
      }
      
      if (!primaryLogo && !alternativeLogo) {
        console.log('No logo files selected, skipping file uploads');
      }
      
      showNotification('Company settings updated successfully', 'success');
      
      // Clear file input and logo data
      setPrimaryLogo(null);
      setAlternativeLogo(null);
      if (primaryLogoInputRef.current) {
        primaryLogoInputRef.current.value = '';
      }
      if (alternativeLogoInputRef.current) {
        alternativeLogoInputRef.current.value = '';
      }
      
      // Refresh the page to show the new logo
      window.location.reload();
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating company settings:', err);
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        
        if (err.response.data && err.response.data.message) {
          showNotification(`Error: ${err.response.data.message}`, 'error');
        } else {
          showNotification(`Server error: ${err.response.status}`, 'error');
        }
      } else if (err.request) {
        // The request was made but no response was received
        showNotification('No response from server. Network issue?', 'error');
      } else {
        // Something happened in setting up the request that triggered an Error
        showNotification(`Request error: ${err.message}`, 'error');
      }
      setLoading(false);
    }
  };
  
  // Handle primary logo deletion prompt
  const handleDeletePrimaryLogo = () => {
    setShowDeletePrimaryLogoConfirmation(true);
  };
  
  // Execute primary logo deletion
  const executeDeletePrimaryLogo = async () => {
    setDeletePrimaryLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete('/api/settings/company/logo', config);
      
      // Clear logo preview
      setPrimaryLogoPreview('');
      setPrimaryLogo(null);
      if (primaryLogoInputRef.current) {
        primaryLogoInputRef.current.value = '';
      }
      
      // Reload the page to ensure clean state after logo deletion
      // This prevents the "Unexpected end of form" error when uploading a new logo
      window.location.reload();
      
      showNotification('Primary logo deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting primary logo:', err);
      showNotification('Failed to delete primary logo', 'error');
    } finally {
      setDeletePrimaryLoading(false);
    }
  };
  
  // Handle alternative logo deletion prompt
  const handleDeleteAlternativeLogo = () => {
    setShowDeleteAlternativeLogoConfirmation(true);
  };
  
  // Execute alternative logo deletion
  const executeDeleteAlternativeLogo = async () => {
    setDeleteAlternativeLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete('/api/settings/company/logo-alt', config);
      
      // Clear alternative logo preview
      setAlternativeLogoPreview('');
      setAlternativeLogo(null);
      if (alternativeLogoInputRef.current) {
        alternativeLogoInputRef.current.value = '';
      }
      
      // Reload the page to ensure clean state after logo deletion
      window.location.reload();
      
      showNotification('Alternative logo deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting alternative logo:', err);
      showNotification('Failed to delete alternative logo', 'error');
    } finally {
      setDeleteAlternativeLoading(false);
    }
  };
  
  if (loading && !formData.company_name) {
    return <div className="text-center py-4">Loading settings...</div>;
  }
  
  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Company Settings</h3>
      
      {/* Save Confirmation Modal */}
      <SaveConfirmationModal
        isOpen={showSaveConfirmation}
        onClose={() => setShowSaveConfirmation(false)}
        onConfirm={() => {
          setShowSaveConfirmation(false);
          executeSave();
        }}
        title="Save Company Settings"
        message="Are you sure you want to save these company settings? The page will reload to apply the changes."
      />
      
      {/* Delete Primary Logo Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeletePrimaryLogoConfirmation}
        onClose={() => setShowDeletePrimaryLogoConfirmation(false)}
        onConfirm={() => {
          setShowDeletePrimaryLogoConfirmation(false);
          executeDeletePrimaryLogo();
        }}
        title="Delete Primary Logo"
        message="Are you sure you want to delete the primary logo? This action cannot be undone."
      />
      
      {/* Delete Alternative Logo Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteAlternativeLogoConfirmation}
        onClose={() => setShowDeleteAlternativeLogoConfirmation(false)}
        onConfirm={() => {
          setShowDeleteAlternativeLogoConfirmation(false);
          executeDeleteAlternativeLogo();
        }}
        title="Delete Alternative Logo"
        message="Are you sure you want to delete the alternative logo? This action cannot be undone."
      />
      
      {/* Display error or success messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Save Button - Moved to top for better visibility */}
        <div className="flex justify-end pb-4 border-b border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 font-medium"
          >
            {loading ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Company Name */}
          <div>
            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name
            </label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Company Email */}
          <div>
            <label htmlFor="company_email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="company_email"
              name="company_email"
              value={formData.company_email}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* Company Address */}
        <div>
          <label htmlFor="company_address" className="block text-sm font-medium text-gray-700 mb-1">
            Company Address
          </label>
          <textarea
            id="company_address"
            name="company_address"
            value={formData.company_address}
            onChange={handleChange}
            rows="2"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Phone */}
          <div>
            <label htmlFor="company_phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              id="company_phone"
              name="company_phone"
              value={formData.company_phone}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* VAT Number */}
          <div>
            <label htmlFor="company_vat" className="block text-sm font-medium text-gray-700 mb-1">
              VAT/Tax Number
            </label>
            <input
              type="text"
              id="company_vat"
              name="company_vat"
              value={formData.company_vat}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* Country-Specific Bank Accounts */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Bank Accounts</h3>
            <button
              type="button"
              onClick={() => showBankAccountForm ? cancelBankAccountForm() : setShowBankAccountForm(true)}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
            >
              {showBankAccountForm ? 'Cancel' : 'Add Bank Account'}
            </button>
          </div>
          
          {/* Bank Account Form */}
          {showBankAccountForm && (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                {editingBankAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    value={bankAccountForm.country_code}
                    onChange={(e) => setBankAccountForm({...bankAccountForm, country_code: e.target.value})}
                    disabled={editingBankAccount}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Country</option>
                    {bankAccountCountries.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name} ({country.code})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Account
                  </label>
                  <input
                    type="text"
                    value={bankAccountForm.bank_account}
                    onChange={(e) => setBankAccountForm({...bankAccountForm, bank_account: e.target.value})}
                    placeholder="Account number or IBAN"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SWIFT/BIC
                  </label>
                  <input
                    type="text"
                    value={bankAccountForm.swift_bic}
                    onChange={(e) => setBankAccountForm({...bankAccountForm, swift_bic: e.target.value})}
                    placeholder="SWIFT/BIC code"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={bankAccountForm.bank_name}
                    onChange={(e) => setBankAccountForm({...bankAccountForm, bank_name: e.target.value})}
                    placeholder="Bank name (optional)"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={bankAccountForm.currency}
                    onChange={(e) => setBankAccountForm({...bankAccountForm, currency: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="NOK">NOK</option>
                    <option value="SEK">SEK</option>
                    <option value="DKK">DKK</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={bankAccountForm.is_default}
                      onChange={(e) => setBankAccountForm({...bankAccountForm, is_default: e.target.checked})}
                      className="mr-2"
                    />
                    Default account
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end mt-4 space-x-2">
                <button
                  type="button"
                  onClick={cancelBankAccountForm}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveBankAccount}
                  disabled={savingBankAccount}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {savingBankAccount ? 'Saving...' : (editingBankAccount ? 'Update Account' : 'Save Account')}
                </button>
              </div>
            </div>
          )}
          
          {/* Bank Accounts List */}
          <div className="space-y-2">
            {bankAccounts.length > 0 ? (
              bankAccounts.map((account) => (
                <div key={account.country_code} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-gray-900">
                        {bankAccountCountries.find(c => c.code === account.country_code)?.name || account.country_code}
                      </span>
                      {account.is_default && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="mr-4">Account: {account.bank_account}</span>
                      {account.swift_bic && <span className="mr-4">SWIFT: {account.swift_bic}</span>}
                      {account.currency && <span>Currency: {account.currency}</span>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => editBankAccount(account)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBankAccount(account.country_code)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No bank accounts configured. Click "Add Bank Account" to get started.
              </div>
            )}
          </div>
          
          {/* Legacy fallback notice */}
          {(formData.company_bank_account || formData.company_swift) && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Legacy bank account detected:</strong> {formData.company_bank_account} 
                {formData.company_swift && ` (SWIFT: ${formData.company_swift})`}
                <br />
                This will be used as fallback when no country-specific account is found.
              </p>
            </div>
          )}
        </div>
        
        {/* About the company */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="company_about" className="block text-sm font-medium text-gray-700">
              About the company
            </label>
            <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 border border-indigo-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
              AI-powered
            </span>
          </div>
          <textarea
            id="company_about"
            name="company_about"
            value={formData.company_about}
            onChange={handleChange}
            rows="4"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Write a brief description of your company, services, values, and target audience. This information will be used by AI to generate personalized marketing emails."
          />
          <p className="mt-1 text-xs text-gray-500">
            This description helps our AI understand your company better for generating personalized marketing emails and content. Include details about your services, unique selling points, and target audience for best results.
          </p>
        </div>
        
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h4 className="font-medium text-gray-800 mb-3">Company Logos</h4>
          
          {/* Primary Logo */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary Logo
            </label>
            
            <div className="flex items-start space-x-4">
              {/* Logo Preview */}
              <div className="mt-1 w-48 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                {primaryLogoPreview ? (
                  <img 
                    src={primaryLogoPreview} 
                    alt="Primary Logo Preview" 
                    className="max-w-full max-h-20 object-contain" 
                    onError={(e) => {
                      console.error("Logo load error:", e);
                      console.log("Logo source:", primaryLogoPreview);
                    }}
                  />
                ) : (
                  <div className="text-gray-400 text-sm text-center">No logo uploaded</div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1">
                <input
                  type="file"
                  ref={primaryLogoInputRef}
                  onChange={handlePrimaryLogoChange}
                  accept="image/*"
                  className="sr-only"
                  id="primary-logo-upload"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="primary-logo-upload"
                    className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    Select Logo
                  </label>
                  
                  {/* Show Remove button for new logo selection */}
                  {primaryLogo && (
                    <button
                      type="button"
                      onClick={() => {
                        setPrimaryLogo(null);
                        setPrimaryLogoPreview('');
                        if (primaryLogoInputRef.current) {
                          primaryLogoInputRef.current.value = '';
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                  
                  {/* Show Delete button for existing logo */}
                  {!primaryLogo && primaryLogoPreview && !primaryLogoPreview.startsWith('blob:') && (
                    <button
                      type="button"
                      onClick={handleDeletePrimaryLogo}
                      disabled={deletePrimaryLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
                    >
                      {deletePrimaryLoading ? 'Deleting...' : 'Delete Logo'}
                    </button>
                  )}
                </div>
                
                <p className="mt-2 text-xs text-gray-500">
                  Upload your main company logo (recommended size: 240x80px, PNG or JPG format)
                </p>
              </div>
            </div>
          </div>
          
          {/* Alternative Logo */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alternative Logo
            </label>
            
            <div className="flex items-start space-x-4">
              {/* Alternative Logo Preview */}
              <div className="mt-1 w-48 h-24 border border-gray-300 rounded-md flex items-center justify-center bg-gray-50">
                {alternativeLogoPreview ? (
                  <img 
                    src={alternativeLogoPreview} 
                    alt="Alternative Logo Preview" 
                    className="max-w-full max-h-20 object-contain" 
                    onError={(e) => {
                      console.error("Alternative logo load error:", e);
                      console.log("Alternative logo source:", alternativeLogoPreview);
                    }}
                  />
                ) : (
                  <div className="text-gray-400 text-sm text-center">No logo uploaded</div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1">
                <input
                  type="file"
                  ref={alternativeLogoInputRef}
                  onChange={handleAlternativeLogoChange}
                  accept="image/*"
                  className="sr-only"
                  id="alternative-logo-upload"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="alternative-logo-upload"
                    className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    Select Logo
                  </label>
                  
                  {/* Show Remove button for new alternative logo selection */}
                  {alternativeLogo && (
                    <button
                      type="button"
                      onClick={() => {
                        setAlternativeLogo(null);
                        setAlternativeLogoPreview('');
                        if (alternativeLogoInputRef.current) {
                          alternativeLogoInputRef.current.value = '';
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                  
                  {/* Show Delete button for existing alternative logo */}
                  {!alternativeLogo && alternativeLogoPreview && !alternativeLogoPreview.startsWith('blob:') && (
                    <button
                      type="button"
                      onClick={handleDeleteAlternativeLogo}
                      disabled={deleteAlternativeLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
                    >
                      {deleteAlternativeLoading ? 'Deleting...' : 'Delete Logo'}
                    </button>
                  )}
                </div>
                
                <p className="mt-2 text-xs text-gray-500">
                  Upload an alternative company logo (recommended size: 300x100px, PNG or JPG format)
                </p>
              </div>
            </div>
          </div>
          
          {/* Logo Usage Configuration */}
          <div className="mt-6">
            <h4 className="font-medium text-gray-800 mb-3">Logo Usage Configuration</h4>
            <p className="text-sm text-gray-500 mb-4">
              Configure which logo to use in different parts of the application
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Sidebar Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sidebar (Nav Bar) Logo
                </label>
                <div className="flex items-center space-x-4">
                  <select 
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={logoUsage.sidebar}
                    onChange={(e) => handleLogoUsageChange('sidebar', e.target.value)}
                  >
                    <option value="primary">Primary Logo</option>
                    <option value="alternative">Alternative Logo</option>
                  </select>
                </div>
              </div>
              
              {/* Invoices Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoices Logo
                </label>
                <div className="flex items-center space-x-4">
                  <select 
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={logoUsage.invoices}
                    onChange={(e) => handleLogoUsageChange('invoices', e.target.value)}
                  >
                    <option value="primary">Primary Logo</option>
                    <option value="alternative">Alternative Logo</option>
                  </select>
                </div>
              </div>
              
              {/* Email Marketing Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Marketing Logo
                </label>
                <div className="flex items-center space-x-4">
                  <select 
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={logoUsage.email}
                    onChange={(e) => handleLogoUsageChange('email', e.target.value)}
                  >
                    <option value="primary">Primary Logo</option>
                    <option value="alternative">Alternative Logo</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sidebar Color Configuration */}
          <div className="mt-6">
            <h4 className="font-medium text-gray-800 mb-3">Sidebar Color</h4>
            <p className="text-sm text-gray-500 mb-4">
              Customize the sidebar color to match your brand
            </p>
            
            <div className="flex items-center space-x-4">
              <input
                type="color"
                id="sidebar_color"
                name="sidebar_color"
                value={sidebarColor}
                onChange={(e) => setSidebarColor(e.target.value)}
                className="w-10 h-10 border-none rounded cursor-pointer"
              />
              <label htmlFor="sidebar_color" className="text-sm font-medium text-gray-700">
                Sidebar Color: {sidebarColor}
              </label>
            </div>
          </div>
        </div>
      </form>

      {/* Company Countries Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Company Countries</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select countries that are relevant for your business. These will be available for selection throughout the application.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {availableCountries.map(country => (
            <div key={country} className="flex items-center">
              <input
                type="checkbox"
                id={`country-${country}`}
                checked={companyCountries.includes(country)}
                onChange={() => {
                  if (companyCountries.includes(country)) {
                    setCompanyCountries(companyCountries.filter(c => c !== country));
                  } else {
                    setCompanyCountries([...companyCountries, country]);
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={`country-${country}`} className="ml-2 block text-sm text-gray-900">
                {country}
              </label>
            </div>
          ))}
        </div>

      </div>

      {/* Removed Webhook URL Section */}
    </div>
  );
};

export default CompanySettings;
